import { FunctionComponent, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

interface ScoreSaveBarProps {
	dirty: boolean;
	saving?: boolean;
	error?: string | null;
	onSave: () => void;
	onDiscard: () => void;
	/** Extra controls between the message and the buttons (a status pill, a test button). */
	children?: ReactNode;
}

/**
 * The bar pinned to the bottom of every settings page while it has unsaved
 * changes (PREC-4055). Hidden while the page is clean, so a page that is only
 * being read has nothing to dismiss.
 */
export const ScoreSaveBar: FunctionComponent<ScoreSaveBarProps> = ({
	dirty,
	saving = false,
	error,
	onSave,
	onDiscard,
	children,
}) => {
	const { t } = useTranslation();

	if (!dirty && !saving) {
		return null;
	}

	return (
		<div
			className="sticky -bottom-4 z-[110] -mx-4 -mb-12 mt-6 flex items-center gap-3 border-t border-border-light bg-white px-4 pb-[26px] pt-2.5 shadow-[0_-4px_16px_rgba(16,24,40,0.06)]"
			data-testid="score-save-bar"
		>
			<span className="h-2 w-2 shrink-0 rounded-full bg-status-warning" />
			<span className="text-sm text-text-meta">{t("leadScoring.unsavedChanges")}</span>
			{error && (
				<span className="text-sm text-status-error" data-testid="score-save-error">
					{error}
				</span>
			)}
			<div className="ml-auto flex items-center gap-2">
				{children}
				<Button
					color={ButtonColor.ACTION_SECONDARY}
					disabled={saving}
					size={ButtonSize.XS}
					testId="score-discard"
					title={t("leadScoring.shell.discardChanges")}
					type="button"
					onClick={onDiscard}
				/>
				<Button
					color={ButtonColor.ACTION}
					disabled={!dirty}
					isLoading={saving}
					size={ButtonSize.XS}
					testId="score-save"
					title={t("leadScoring.shell.saveChanges")}
					type="button"
					onClick={onSave}
				/>
			</div>
		</div>
	);
};
